/**
 * Hardware product identity. Independent of slab placement metadata.
 *
 * The slab SVG (`#hardware-guides`) tells JARVIS where a product may go.
 * `hardwareCompatibility.ts` tells JARVIS which combinations are valid.
 * This catalogue tells JARVIS which products exist, which range they belong
 * to, and which reusable SVG they use. It must not store coordinates.
 *
 * Artwork lives under `public/images/door-designer/hardware/{range}/`.
 * Placement coordinates stay on the selected slab, not here.
 */

import type { HardwarePosition, HardwareRoleId, PullBarLengthMm } from './hardwareCompatibility';
import { PULL_BAR_LENGTHS_MM } from './hardwareCompatibility';

export const HARDWARE_ASSET_ROOT = '/images/door-designer/hardware';

export type HardwareRangeId = 'stainless-steel' | 'classic' | 'premium';

export interface HardwareRange {
  id: HardwareRangeId;
  label: string;
}

export const HARDWARE_RANGES: HardwareRange[] = [
  { id: 'stainless-steel', label: 'Stainless Steel' },
  { id: 'classic', label: 'Classic' },
  { id: 'premium', label: 'Premium' },
];

export const STAINLESS_STEEL_RANGE_ID: HardwareRangeId = 'stainless-steel';
export const CLASSIC_RANGE_ID: HardwareRangeId = 'classic';
export const PREMIUM_RANGE_ID: HardwareRangeId = 'premium';

export type HardwareColourFamilyId =
  | 'chrome'
  | 'graphite'
  | 'gold'
  | 'black'
  | 'forged-black'
  | 'pewter'
  | 'satin-brass'
  | 'bronze';

export interface HardwareColourFamily {
  id: HardwareColourFamilyId;
  label: string;
  swatchHex: string;
  /** Optional metallic fill. Falls back to `swatchHex`. */
  swatchBackground?: string;
}

export const DEFAULT_HARDWARE_COLOUR_FAMILY_ID: HardwareColourFamilyId = 'graphite';

export const HARDWARE_COLOUR_FAMILIES: HardwareColourFamily[] = [
  {
    id: 'chrome',
    label: 'Chrome',
    swatchHex: '#e8e8e8',
    swatchBackground:
      'linear-gradient(125deg, #ffffff 0%, #f2f2f2 18%, #d6d6d6 42%, #ffffff 58%, #e8e8e8 78%, #cfcfcf 100%)',
  },
  { id: 'graphite', label: 'Graphite', swatchHex: '#d5d8dc' },
  { id: 'gold', label: 'Gold', swatchHex: '#c9a227' },
  { id: 'black', label: 'Black', swatchHex: '#1c1c1c' },
  { id: 'forged-black', label: 'Forged Black', swatchHex: '#2c2c2a' },
  { id: 'pewter', label: 'Pewter', swatchHex: '#8a8d86' },
  { id: 'satin-brass', label: 'Satin Brass', swatchHex: '#c5a35a' },
  { id: 'bronze', label: 'Bronze', swatchHex: '#6e4b32' },
];

export type HardwareFamilyId =
  | 'lever'
  | 'pull-bar'
  | 'locking'
  | 'letterplate'
  | 'knocker'
  | 'spyhole';

/** Intended size in authored slab space (the same 780 × 1975 units as door-slab artwork). */
export interface HardwareRenderSize {
  width: number;
  height: number;
}

export interface HardwareProduct {
  id: string;
  name: string;
  family: HardwareFamilyId;
  role: HardwareRoleId;
  publicPath: string;
  /**
   * Customer-facing collection. A range can own a lever, pull bar, knocker,
   * letterplate, and so on. Omit until the product belongs to a named range.
   */
  rangeId?: HardwareRangeId;
  /**
   * Metal finish family. Omit until the product is authored in a named finish.
   */
  colourFamilyId?: HardwareColourFamilyId;
  /**
   * Painted size in slab space. The SVG viewBox is only the artwork's
   * internal coordinate system — it is not millimetres on the door.
   * Omit to fall back to the viewBox width/height.
   */
  renderSize?: HardwareRenderSize;
  /** Present only on pull-bar products. Length is a product identity, not a coordinate. */
  pullBarLengthMm?: PullBarLengthMm;
  /** Present when the bar section is not round. Identity, not a coordinate. */
  pullBarProfile?: 'flat';
  /** Present when the product is authored for a specific lock position. */
  position?: HardwarePosition;
}

function hardwarePublicPath(relativePath: string): string {
  return `${HARDWARE_ASSET_ROOT}/${relativePath}`;
}

/** Placeholder lever used by tests until a range product is authored. */
export const STANDARD_HANDLE_PRODUCT_ID = 'lever-long-backplate';

/** Placeholder pull bar used by tests until a range product is authored. */
export const PREMIUM_PULL_BAR_PRODUCT_ID = 'pull-bar-400';

/** Authored Stainless Steel chrome 400mm flat pull bar. Places on the slab `pullbar-anchor`. */
export const PREMIUM_CHROME_FLAT_400_PRODUCT_ID = 'chrome-flat-400';

/** Authored Stainless Steel chrome 900mm flat pull bar. Places on the slab `pullbar-anchor`. */
export const PREMIUM_CHROME_FLAT_900_PRODUCT_ID = 'chrome-flat-900';

/** Authored Stainless Steel chrome 1200mm flat pull bar. Places on the slab `pullbar-anchor`. */
export const PREMIUM_CHROME_FLAT_1200_PRODUCT_ID = 'chrome-flat-1200';

/** Authored Stainless Steel chrome 1800mm flat pull bar. Places on the slab `pullbar-anchor`. */
export const PREMIUM_CHROME_FLAT_1800_PRODUCT_ID = 'chrome-flat-1800';

/** Authored Classic chrome letterplate. Places on a slab letterplate zone. */
export const CLASSIC_CHROME_LETTERPLATE_PRODUCT_ID = 'chrome-classic-letterplate';

/** Authored Classic chrome escutcheon. Places on slab `standard-anchor` or `heritage-anchor` and pairs with a bar. */
export const CLASSIC_CHROME_ESCUTCHEON_PRODUCT_ID = 'chrome-classic-escutcheon';

function stainlessSteelChromeFlatBar(lengthMm: PullBarLengthMm): HardwareProduct {
  return {
    id: `chrome-flat-${lengthMm}`,
    name: `Chrome flat pull bar ${lengthMm}mm`,
    family: 'pull-bar',
    role: 'pull-bar',
    rangeId: STAINLESS_STEEL_RANGE_ID,
    colourFamilyId: 'chrome',
    publicPath: hardwarePublicPath(`stainless-steel/chrome-flat-${lengthMm}.svg`),
    pullBarLengthMm: lengthMm,
    pullBarProfile: 'flat',
  };
}

/** Placeholder classic escutcheon. Pairs with a bar handle. */
export const CLASSIC_ESCUTCHEON_PRODUCT_ID = 'classic-escutcheon';

export const HARDWARE_PRODUCTS: HardwareProduct[] = [
  ...PULL_BAR_LENGTHS_MM.map(stainlessSteelChromeFlatBar),
  {
    id: CLASSIC_CHROME_LETTERPLATE_PRODUCT_ID,
    name: 'Chrome classic letterplate',
    family: 'letterplate',
    role: 'letterplate',
    rangeId: CLASSIC_RANGE_ID,
    colourFamilyId: 'chrome',
    publicPath: hardwarePublicPath('classic/chrome-classic-letterplate.svg'),
  },
  {
    id: CLASSIC_CHROME_ESCUTCHEON_PRODUCT_ID,
    name: 'Chrome classic escutcheon',
    family: 'locking',
    role: 'escutcheon',
    rangeId: CLASSIC_RANGE_ID,
    colourFamilyId: 'chrome',
    publicPath: hardwarePublicPath('classic/chrome-classic-escutcheon.svg'),
    position: 'standard',
  },
  {
    id: STANDARD_HANDLE_PRODUCT_ID,
    name: 'Lever long backplate',
    family: 'lever',
    role: 'lever-handle',
    publicPath: hardwarePublicPath('handles/lever/lever-long-backplate.svg'),
    position: 'standard',
  },
  {
    id: 'lever-short-backplate',
    name: 'Lever short backplate',
    family: 'lever',
    role: 'lever-handle',
    publicPath: hardwarePublicPath('handles/lever/lever-short-backplate.svg'),
  },
  {
    id: 'lever-on-rose',
    name: 'Lever on rose',
    family: 'lever',
    role: 'lever-on-rose',
    publicPath: hardwarePublicPath('handles/lever/lever-on-rose.svg'),
  },
  ...PULL_BAR_LENGTHS_MM.map((lengthMm) => ({
    id: `pull-bar-${lengthMm}`,
    name: `Pull bar ${lengthMm} mm`,
    family: 'pull-bar' as const,
    role: 'pull-bar' as const,
    publicPath: hardwarePublicPath(`handles/pull-bars/pull-bar-${lengthMm}.svg`),
    pullBarLengthMm: lengthMm,
  })),
  {
    id: 'rim-cylinder',
    name: 'Rim cylinder',
    family: 'locking',
    role: 'rim-cylinder',
    publicPath: hardwarePublicPath('locking/rim-cylinder.svg'),
  },
  {
    id: 'finger-pull-escutcheon',
    name: 'Finger-pull escutcheon',
    family: 'locking',
    role: 'finger-pull-escutcheon',
    publicPath: hardwarePublicPath('locking/finger-pull-escutcheon.svg'),
    position: 'standard',
  },
  {
    id: 'heritage-finger-pull-escutcheon',
    name: 'Heritage finger-pull escutcheon',
    family: 'locking',
    role: 'finger-pull-escutcheon',
    publicPath: hardwarePublicPath('locking/heritage-finger-pull-escutcheon.svg'),
    position: 'heritage',
  },
  {
    id: CLASSIC_ESCUTCHEON_PRODUCT_ID,
    name: 'Escutcheon',
    family: 'locking',
    role: 'escutcheon',
    publicPath: hardwarePublicPath('locking/classic-escutcheon.svg'),
    position: 'standard',
  },
  {
    id: 'standard-letterplate',
    name: 'Standard letterplate',
    family: 'letterplate',
    role: 'letterplate',
    publicPath: hardwarePublicPath('letterplates/standard-letterplate.svg'),
  },
  {
    id: 'urn-knocker',
    name: 'Urn knocker',
    family: 'knocker',
    role: 'knocker',
    publicPath: hardwarePublicPath('knockers/urn-knocker.svg'),
  },
  {
    id: 'ring-knocker',
    name: 'Ring knocker',
    family: 'knocker',
    role: 'knocker',
    publicPath: hardwarePublicPath('knockers/ring-knocker.svg'),
  },
  {
    id: 'standard-spyhole',
    name: 'Standard spyhole',
    family: 'spyhole',
    role: 'spyhole',
    publicPath: hardwarePublicPath('spyholes/standard-spyhole.svg'),
  },
];

export function getHardwareRange(id: string | undefined): HardwareRange | undefined {
  if (!id) return undefined;
  return HARDWARE_RANGES.find((range) => range.id === id);
}

export function getHardwareProduct(id: string | undefined): HardwareProduct | undefined {
  if (!id) return undefined;
  return HARDWARE_PRODUCTS.find((product) => product.id === id);
}

export function hardwareProductsByRole(role: HardwareRoleId): HardwareProduct[] {
  return HARDWARE_PRODUCTS.filter((product) => product.role === role);
}

export function hardwareProductsByFamily(family: HardwareFamilyId): HardwareProduct[] {
  return HARDWARE_PRODUCTS.filter((product) => product.family === family);
}

export function hardwareProductsByRange(rangeId: HardwareRangeId): HardwareProduct[] {
  return HARDWARE_PRODUCTS.filter((product) => product.rangeId === rangeId);
}

export function getHardwareColourFamily(
  id: string | undefined
): HardwareColourFamily | undefined {
  if (!id) return undefined;
  return HARDWARE_COLOUR_FAMILIES.find((family) => family.id === id);
}

export function hardwareProductsByRangeAndColour(
  rangeId: HardwareRangeId,
  colourFamilyId: HardwareColourFamilyId
): HardwareProduct[] {
  return HARDWARE_PRODUCTS.filter(
    (product) => product.rangeId === rangeId && product.colourFamilyId === colourFamilyId
  );
}

/** Untagged products stay listed until they are authored in a named finish. */
export function hardwareProductMatchesColourFamily(
  product: HardwareProduct,
  colourFamilyId: HardwareColourFamilyId
): boolean {
  return !product.colourFamilyId || product.colourFamilyId === colourFamilyId;
}

export function hardwareProductsByFamilyAndColour(
  family: HardwareFamilyId,
  colourFamilyId: HardwareColourFamilyId
): HardwareProduct[] {
  return HARDWARE_PRODUCTS.filter(
    (product) =>
      product.family === family && hardwareProductMatchesColourFamily(product, colourFamilyId)
  );
}
